import { getFunctions, httpsCallable } from "firebase/functions";

// A rewrite re-emits the entire input, so latency scales with length (long
// inputs are chunked server-side and run sequentially). The 60s callable
// default is not enough for a full encounter transcript.
const CALLABLE_TIMEOUT_MS = 180000;

// Resolved on first call, not at import: this module is pulled in by the
// ChartMind recording step, which can be imported before initializeApp() runs.
let rewriteCallable = null;

function getRewriteCallable() {
  if (!rewriteCallable) {
    rewriteCallable = httpsCallable(
      getFunctions(),
      "documentrewriter_rewrite",
      { timeout: CALLABLE_TIMEOUT_MS },
    );
  }
  return rewriteCallable;
}

// The callable SDK collapses several very different failures into a bare
// "internal" — most misleadingly, a function that isn't deployed at all (the
// 404 has no error body to decode). Name the cases that have distinct fixes.
function describeCallableError(error) {
  const code = String(error?.code || "").replace(/^functions\//, "");
  const message = String(error?.message || "").trim();

  if (code === "not-found") {
    return "the rewrite function is not deployed to this environment";
  }
  if (code === "unauthenticated") {
    return "you are signed out";
  }
  if (code === "deadline-exceeded") {
    return "the rewrite timed out";
  }
  if (code === "internal" && /^internal$/i.test(message)) {
    // Either the function threw and Firebase stripped the message, or it isn't
    // deployed — both surface identically here, so point at both.
    return "the rewrite function returned an internal error, or is not deployed to this environment";
  }
  return message || code || "unknown error";
}

/**
 * Rewrite inline text via the document rewriter (chartmind/document_rewriter),
 * normalizing confusing clinical negations into plain language.
 *
 * @param {string} text  Raw text/Markdown to clean up.
 * @param {{categories?: string[]|null}} options  Subset of the negation
 *   catalog to normalize; empty/absent means the whole catalog.
 * @returns {Promise<{rewrittenText: string, categories: string[], model: string|null}>}
 */
export async function rewriteText(text, { categories = null } = {}) {
  const payload = { text: String(text || "") };
  if (Array.isArray(categories) && categories.length) {
    payload.categories = categories;
  }

  let response;
  try {
    response = await getRewriteCallable()(payload);
  } catch (error) {
    throw new Error(describeCallableError(error));
  }

  const rewrittenText = response?.data?.rewrittenText || "";

  if (!rewrittenText.trim()) {
    throw new Error("The rewrite service returned no text.");
  }

  return {
    rewrittenText,
    categories: response?.data?.categories || [],
    model: response?.data?.model || null,
  };
}

export default { rewriteText };
